/**
 * @file        src/utils/imageFallback.js
 * @description Site-wide safety net for <img> tags whose source fails to load —
 *              in practice the hotel suppliers' photo CDNs (a 404 on
 *              sunhotels, a connection reset on rukmini-ct.flixcart.com), which
 *              otherwise leave a broken-image icon on the hotel cards.
 *
 *              One capture-phase listener on window, so no page component has
 *              to change. A failed image is retried ONCE (with no Referer, in
 *              case the CDN refuses hotlinking), then swapped for a neutral
 *              inline placeholder.
 *
 *              It yields to components that handle their own failure
 *              (AirlineLogo, CityIcon, BrandLogo, Blog…): the decision is
 *              deferred, and an image its component has already removed,
 *              re-pointed or hidden is left alone.
 * @author      Kinetika Labs
 * @created     2026-10-01
 * @changelog
 *   - 2026-10-01 Claude — Initial.
 */

// Long enough for a component's own onError state update to commit first.
const DEFER_MS = 150;

const PLACEHOLDER =
  'data:image/svg+xml;charset=utf-8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300">' +
      '<rect width="400" height="300" fill="#e5e7eb"/>' +
      '<g fill="none" stroke="#9ca3af" stroke-width="10" stroke-linejoin="round">' +
      '<rect x="140" y="100" width="120" height="100" rx="10"/>' +
      '<path d="M150 190l35-40 25 25 20-20 25 35"/>' +
      '</g><circle cx="230" cy="125" r="10" fill="#9ca3af"/></svg>',
  );

function handleImageError(event) {
  const img = event.target;
  if (!(img instanceof HTMLImageElement)) return;
  if (img.dataset.nhFallback === 'done') return;

  const failedSrc = img.getAttribute('src');
  if (!failedSrc || failedSrc.startsWith('data:')) return;

  setTimeout(() => {
    // The component handled it (unmounted it, changed its src, or hid it).
    if (!img.isConnected || img.getAttribute('src') !== failedSrc) return;
    if (img.style.display === 'none') return;

    if (img.dataset.nhFallback !== 'retried') {
      img.dataset.nhFallback = 'retried';
      img.referrerPolicy = 'no-referrer';
      img.src = failedSrc;
      return;
    }
    img.dataset.nhFallback = 'done';
    img.src = PLACEHOLDER;
  }, DEFER_MS);
}

/** Install once, before the app renders. */
export function installImageFallback() {
  window.addEventListener('error', handleImageError, true);
}
